import { useEffect } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { PaymentService } from '../../application/services/PaymentService';
import {
  normalizePaymentStatus,
  PAYMENT_FAILURE_STATUSES,
  PAYMENT_SUCCESS_STATUSES,
} from '../../constants/paymentStatus';
import {
  readPaymentContextSession,
  savePaymentContextSession,
} from '../utils/paymentContextSession';
import { buildPaymentContextResultUrl } from '../utils/paymentResultUrl';
import type { UpiIntentPollingContext } from './paymentHook.types';

const UPI_INTENT_POLL_INTERVAL_MS = 3000;
const UPI_INTENT_MAX_POLL_DURATION_MS = 60000;

interface UseUpiIntentPollingOptions {
  amount?: number;
  isUpiIntentPending: boolean;
  navigate: NavigateFunction;
  service: PaymentService;
  upiIntentPollingContext: UpiIntentPollingContext | null;
  setIsUpiIntentPending: (isPending: boolean) => void;
  setUpiIntentPollingContext: (context: UpiIntentPollingContext | null) => void;
}

export function useUpiIntentPolling({
  amount,
  isUpiIntentPending,
  navigate,
  service,
  upiIntentPollingContext,
  setIsUpiIntentPending,
  setUpiIntentPollingContext,
}: UseUpiIntentPollingOptions) {
  useEffect(() => {
    if (!isUpiIntentPending || !upiIntentPollingContext?.txnId) return;

    let cancelled = false;
    let timeoutId: number | undefined;
    const startedAt = Date.now();

    const navigateToFailure = (
      reason: string,
      txnId = upiIntentPollingContext.txnId,
      paymentSource = upiIntentPollingContext.paymentSource
    ) => {
      setIsUpiIntentPending(false);
      setUpiIntentPollingContext(null);
      navigate(
        buildPaymentContextResultUrl(
          '/payment-context/failure',
          paymentSource,
          txnId
        ),
        {
          replace: true,
          state: {
            reason,
            amount,
          },
        }
      );
    };

    const poll = async () => {
      if (cancelled) return;

      try {
        const response = await service.verifyPaymentContext(upiIntentPollingContext.txnId);
        const status = normalizePaymentStatus(response.paymentStatusStr || 'PENDING');
        const responseTxnId = response.txnId || upiIntentPollingContext.txnId;
        const responsePaymentSource = response.paymentSource || upiIntentPollingContext.paymentSource;

        const currentSession = readPaymentContextSession();
        if (currentSession) {
          savePaymentContextSession({
            ...currentSession,
            txnId: responseTxnId,
            paymentSource: responsePaymentSource,
            ...(response.expiresAt ? { expiresAt: response.expiresAt } : {}),
          });
        }

        if (PAYMENT_SUCCESS_STATUSES.has(status)) {
          setIsUpiIntentPending(false);
          setUpiIntentPollingContext(null);
          navigate(
            buildPaymentContextResultUrl('/payment-context/success', responsePaymentSource, responseTxnId),
            { replace: true }
          );
          return;
        }

        if (PAYMENT_FAILURE_STATUSES.has(status)) {
          navigateToFailure('Payment could not be completed.', responseTxnId, responsePaymentSource);
          return;
        }
      } catch {
        // Keep polling until the one-minute window closes.
      }

      if (Date.now() - startedAt >= UPI_INTENT_MAX_POLL_DURATION_MS) {
        navigateToFailure('Payment verification timed out.');
        return;
      }

      timeoutId = window.setTimeout(poll, UPI_INTENT_POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [
    amount,
    isUpiIntentPending,
    navigate,
    service,
    setIsUpiIntentPending,
    setUpiIntentPollingContext,
    upiIntentPollingContext,
  ]);
}
